<template>
  <header class="navbar">
    <button
      @click="toggleSidebarCollapsed"
      class="sidebar-toggle-btn"
      :class="{ 'sidebar-collapsed': isSidebarCollapsed }"
      :title="
        isSidebarCollapsed ? 'Étendre la sidebar' : 'Rétracter la sidebar'
      "
    >
      <i class="fas fa-bars"></i>
    </button>

    <div class="navbar-search-group">
      <AdminAcheteurSearch />
      <AdminVendeurSearch />
    </div>

    <div class="navbar-nav">
      <div class="us-alerts" ref="usAlertsRef">
        <button
          type="button"
          class="us-alerts-button"
          aria-label="Notifications administrateur"
          :aria-expanded="isUsAlertsOpen"
          @click.stop="toggleUsAlerts"
        >
          <i class="fas fa-bell"></i>
          <span v-if="adminNotifications.length" class="us-alerts-badge">
            {{ adminNotifications.length > 99 ? "99+" : adminNotifications.length }}
          </span>
        </button>
        <div v-if="isUsAlertsOpen" class="us-alerts-menu">
          <div class="us-alerts-title">Notifications</div>
          <p v-if="usAlertsLoading" class="us-alerts-empty">Chargement…</p>
          <p v-else-if="!adminNotifications.length" class="us-alerts-empty">
            Aucune notification à traiter.
          </p>
          <div
            v-for="notification in adminNotifications"
            v-else
            :key="notification.idNotification"
            class="us-alert-item"
            :class="{ 'us-alert-item-unread': !notification.isRead }"
          >
            <div class="us-alert-summary">
              <span class="us-alert-order">{{ notification.title }}</span>
              <span class="us-alert-context">{{ notificationCompactContext(notification) }}</span>
              <span :class="notification.isRead ? 'text-amber-700' : 'text-red-700'">
                {{ notification.isRead ? "Lue · à traiter" : "Non lue" }}
              </span>
            </div>
            <div v-if="isNotificationExpanded(notification.idNotification)" class="us-alert-details">
              <span v-if="notification.message">{{ notification.message }}</span>
              <span v-if="notificationSellerName(notification)">
                Vendeur : {{ notificationSellerName(notification) }}
              </span>
              <span v-if="notificationBuyerName(notification)">
                Acheteur : {{ notificationBuyerName(notification) }}
              </span>
              <span v-if="notificationContact(notification)">
                Contact : {{ notificationContact(notification) }}
              </span>
            </div>
            <div class="us-alert-actions">
              <button
                type="button"
                class="us-alert-secondary"
                @click.stop="toggleNotificationDetails(notification.idNotification)"
              >
                {{ isNotificationExpanded(notification.idNotification) ? "Masquer" : "Voir détails" }}
              </button>
              <button type="button" class="us-alert-secondary" @click="openNotification(notification)">
                Ouvrir
              </button>
              <button
                type="button"
                class="us-alert-resolve"
                @click.stop="resolveNotification(notification.idNotification)"
              >
                Traiter
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="navbar-dropdown" ref="dropdownRef">
        <button
          class="navbar-dropdown-toggle"
          @click="toggleDropdown"
          :aria-expanded="isDropdownOpen"
        >
          <i class="fas fa-user"></i>
        </button>
        <ul
          class="navbar-dropdown-menu"
          :class="isDropdownOpen ? 'is-open' : 'is-closed'"
        >
          <li 
            class="dropdown-item dropdown-logout" 
            @click="adminLogout">
            <i class="fas fa-sign-out-alt"></i>
            <a href="javascript:void(0);">Déconnexion</a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Navbar MOBILE -->
    <div class="navbar-mobile-container">
      <button
        type="button"
        @click="toggleSidebar"
        aria-controls="sidebarMenu"
        :aria-expanded="isSidebarOpen"
        aria-label="Toggle navigation"
        class="navbar-mobile-burger"
      >
        <i class="fas fa-bars"></i>
      </button>
      <ul class="navbar-mobile-buttons">
        <li v-if="vendeurPageUrl">
          <a
            :href="vendeurPageUrl"
            class="navbar-btn-secondary"
            target="_blank"
          >
            <i class="fas fa-store"></i>
            <span>Ma page</span>
          </a>
        </li>
        <li>
          <div class="navbar-dropdown" ref="dropdownMobileRef">
            <button
              class="navbar-dropdown-toggle"
              @click="toggleDropdownMobile"
              :aria-expanded="isDropdownMobileOpen"
            >
              <i class="fas fa-user"></i>
            </button>
            <ul
              class="navbar-dropdown-menu"
              :class="isDropdownMobileOpen ? 'is-open' : 'is-closed'"
            >
              <li v-if="isLoading" class="dropdown-loading">
                <i class="fas fa-spinner fa-spin"></i>
                <span>Chargement des informations...</span>
              </li>
              <template v-else>
                <!-- Logout -->
                <li
                  class="dropdown-item dropdown-logout"
                  @click="
                    () => {
                      isDropdownMobileOpen = false;
                      adminLogout();
                    }
                  "
                >
                  <i class="fas fa-sign-out-alt"></i>
                  <a href="#">Déconnexion</a>
                </li>
              </template>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getVendeurGeneralInfo } from "@/api/vendeur";
import {
  adminEfpGetNotifications,
  adminEfpMarkNotificationRead,
  adminEfpNotificationsStreamUrl,
  adminEfpResolveNotification,
  type AdminEfpNotification,
} from "@/api/adminEfp";
import { useAuthStore } from "@/stores/auth";
import type { NavbarProps, NavbarEmits } from "@/types";
import { useAdminEfpStore } from "@/stores/adminEfp";
import AdminAcheteurSearch from "@/components/adminEFP/AdminAcheteurSearch.vue";
import AdminVendeurSearch from "@/components/adminEFP/AdminVendeurSearch.vue";

const adminEfp      = useAdminEfpStore();
const logoutLoading = ref(false);

defineProps<NavbarProps>();
const emit = defineEmits<NavbarEmits>();

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isDashboardRoute = computed(() => {
  return route.path.startsWith("/dashboard");
});

// Génère un slug URL-friendly à partir d'une chaîne
const getSlug = (str: string): string => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
    .replace(/[^a-z0-9]+/g, "-") // Remplace les caractères spéciaux par des tirets
    .replace(/^-+|-+$/g, ""); // Supprime les tirets en début/fin
};

// URL de la page vendeur
const vendeurPageUrl = computed(() => {
  const nomBoutique = vendeurData.value.nomBoutique;
  const idVendeur = vendeurData.value.id_vendeur;
  if (!nomBoutique || !idVendeur) return "";
  const slug = getSlug(nomBoutique);
  return `https://www.efashion-paris.com/vendeurs/${slug}-${idVendeur}.html`;
});

// Dropdown
const dropdownRef = ref<HTMLElement | null>(null);
const usAlertsRef = ref<HTMLElement | null>(null);
const dropdownMobileRef = ref<HTMLElement | null>(null);
const isDropdownOpen = ref(false);
const isDropdownMobileOpen = ref(false);
const isUsAlertsOpen = ref(false);
const usAlertsLoading = ref(false);
const adminNotifications = ref<AdminEfpNotification[]>([]);
const expandedNotificationIds = ref<Set<number>>(new Set());
let notificationsEventSource: EventSource | null = null;

const handleClickOutside = (event: Event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
  if (usAlertsRef.value && !usAlertsRef.value.contains(event.target as Node)) {
    isUsAlertsOpen.value = false;
  }
  if (
    dropdownMobileRef.value &&
    !dropdownMobileRef.value.contains(event.target as Node)
  ) {
    isDropdownMobileOpen.value = false;
  }
};

const loadUsOrderAlerts = async () => {
  usAlertsLoading.value = true;
  try {
    const response = await adminEfpGetNotifications();
    adminNotifications.value = response.items;
  } catch {
    adminNotifications.value = [];
  } finally {
    usAlertsLoading.value = false;
  }
};

const toggleUsAlerts = () => {
  isUsAlertsOpen.value = !isUsAlertsOpen.value;
  if (isUsAlertsOpen.value) void loadUsOrderAlerts();
};

const formatUsOrderDate = (value: string | null) => {
  if (!value) return "Date inconnue";
  const parsed = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsed);
};

const notificationPayloadValue = (
  notification: AdminEfpNotification,
  key: string,
): string | null => {
  const value = notification.payload?.[key];
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : null;
};

const notificationSellerName = (notification: AdminEfpNotification) =>
  notificationPayloadValue(notification, "sellerName");

const notificationBuyerName = (notification: AdminEfpNotification) =>
  notificationPayloadValue(notification, "buyerName");

const notificationContact = (notification: AdminEfpNotification) =>
  notificationPayloadValue(notification, "email") ||
  notificationPayloadValue(notification, "buyerEmail");

const notificationCompactContext = (notification: AdminEfpNotification) => {
  if (notification.type === "shopify_privacy_request") {
    return notificationPayloadValue(notification, "shopDomain")
      || `Reçue le ${formatUsOrderDate(notification.createdAt)}`;
  }
  if (notification.type === "store_order_created") {
    const product = notificationPayloadValue(notification, "product");
    const amount = notificationPayloadValue(notification, "amount");
    if (product) {
      return amount ? `${product} · ${Number(amount).toFixed(2)} € HT` : product;
    }
  }
  const orderDate = notificationPayloadValue(notification, "orderDate");
  if (orderDate) {
    const destination = notificationPayloadValue(notification, "destinationCode");
    return `Commandée le ${formatUsOrderDate(orderDate)}${destination ? ` · ${destination}` : ""}`;
  }
  const registeredAt = notificationPayloadValue(notification, "registeredAt");
  if (registeredAt) {
    return `Inscrit le ${formatUsOrderDate(registeredAt)}`;
  }
  return `Reçue le ${formatUsOrderDate(notification.createdAt)}`;
};

const isNotificationExpanded = (idNotification: number) =>
  expandedNotificationIds.value.has(idNotification);

const toggleNotificationDetails = (idNotification: number) => {
  const next = new Set(expandedNotificationIds.value);
  if (next.has(idNotification)) next.delete(idNotification);
  else next.add(idNotification);
  expandedNotificationIds.value = next;
};

const openNotification = async (notification: AdminEfpNotification) => {
  if (!notification.isRead) {
    try {
      await adminEfpMarkNotificationRead(notification.idNotification);
      notification.isRead = true;
    } catch {
      // La navigation reste disponible si l'enregistrement de lecture échoue.
    }
  }
  isUsAlertsOpen.value = false;
  if (notification.entityType === "shopify_privacy_request") {
    await router.push({ name: "adminEFP/shopify" });
    return;
  }
  if (notification.entityType === "store_order") {
    await router.push({ name: "adminEFP/store-orders" });
    return;
  }
  if (notification.idCommande) {
    await router.push({
      name: "adminEFP/orders",
      query: { commande_id: String(notification.idCommande) },
    });
  } else if (notification.entityType === "seller" && notification.entityId) {
    await router.push({
      name: "adminEFP/vendeur-fiche",
      params: { id: String(notification.entityId) },
    });
  }
};

const resolveNotification = async (idNotification: number) => {
  await adminEfpResolveNotification(idNotification);
  adminNotifications.value = adminNotifications.value.filter(
    (notification) => notification.idNotification !== idNotification,
  );
};

const connectNotificationsStream = () => {
  notificationsEventSource?.close();
  notificationsEventSource = new EventSource(
    adminEfpNotificationsStreamUrl(),
    { withCredentials: true },
  );
  const reload = () => void loadUsOrderAlerts();
  notificationsEventSource.addEventListener("notification.created", reload);
  notificationsEventSource.addEventListener("notification.read", reload);
  notificationsEventSource.addEventListener("notification.resolved", reload);
};

const DEFAULT_VENDEUR = {
  nomSociete: "non disponible",
  email: "non disponible",
  telephone: "non disponible",
  mobile: "non disponible",
  adresse: "non disponible",
  code_postal: "non disponible",
  nomBoutique: "",
  id_vendeur: 0,
};

const isLoading = ref(true);
const vendeurData = ref({ ...DEFAULT_VENDEUR });

const LOCAL_STORAGE_KEY = "vendeur_info_header";

const loadFromStorage = () => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) {
    isLoading.value = false;
    return;
  }

  try {
    const saved = JSON.parse(raw) as Partial<typeof DEFAULT_VENDEUR>;
    vendeurData.value = {
      ...DEFAULT_VENDEUR,
      ...saved,
    };
  } catch (e) {
    // Erreur silencieuse - les infos vendeur restent vides
  } finally {
    isLoading.value = false;
  }
};


async function adminLogout() {
  logoutLoading.value = true;
  try {
    await adminEfp.logout();
    await router.replace({ name: "adminEFP/auth" });
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    authStore.logout();
  } catch {
    // error.value = "Erreur lors de la déconnexion";
  } finally {
    logoutLoading.value = false;
  }
}


//save grossiste info into local storage and state
const fetchVendeurInfo = async () => {
  const profile = await getVendeurGeneralInfo();

  const storeAddress = profile.adresses?.find((addr: any) => addr.type === 2);

  const newData = {
    nomSociete: profile.nomSociete || profile.nomBoutique || "non disponible",
    email: profile.email || "non disponible",
    telephone: storeAddress?.telephone || "non disponible",
    mobile: storeAddress?.mobile || "non disponible",
    adresse: storeAddress?.adresse || "non disponible",
    code_postal: storeAddress?.codePostal || "non disponible",
    nomBoutique: profile.nomBoutique || "",
    id_vendeur: profile.id_vendeur || 0,
  };

  vendeurData.value = newData;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newData));
};

const refreshFromApiIfNeeded = async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) return;

  const authVendeurId = authStore.user?.id_vendeur ?? authStore.user?.id;
  const cachedId = vendeurData.value.id_vendeur;

  if (
    vendeurData.value.nomBoutique &&
    cachedId &&
    authVendeurId &&
    cachedId === authVendeurId
  ) {
    return;
  }

  isLoading.value = true;
  try {
    await fetchVendeurInfo();
  } catch (error) {
    // Erreur silencieuse - les infos vendeur n'ont pas été chargées
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  loadFromStorage();

  void refreshFromApiIfNeeded();
  void loadUsOrderAlerts();
  connectNotificationsStream();
});

watch(
  () => route.path,
  () => {
    void refreshFromApiIfNeeded();
  },
);

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  notificationsEventSource?.close();
});

const toggleSidebarCollapsed = () => {
  emit("toggle-sidebar-collapsed");
};

const toggleSidebar = () => {
  emit("toggle-sidebar");
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const toggleDropdownMobile = () => {
  isDropdownMobileOpen.value = !isDropdownMobileOpen.value;
};

</script>

<style lang="scss" scoped>
.navbar-search-group {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0 1rem;
  min-width: 0;
}

.us-alerts {
  position: relative;
}

.us-alerts-button {
  position: relative;
  padding: 0.65rem;
  color: #374151;
}

.us-alerts-badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 1.15rem;
  padding: 0 0.2rem;
  border-radius: 9999px;
  background: #dc2626;
  color: white;
  font-size: 0.65rem;
  line-height: 1.15rem;
  text-align: center;
}

.us-alerts-menu {
  position: absolute;
  z-index: 50;
  top: calc(100% + 0.5rem);
  right: 0;
  width: min(24rem, 90vw);
  max-height: 28rem;
  overflow-y: auto;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  background: white;
  box-shadow: 0 10px 25px rgb(0 0 0 / 15%);
}

.us-alerts-title {
  position: sticky;
  top: 0;
  padding: 0.8rem 1rem;
  background: #fef2f2;
  color: #991b1b;
  font-weight: 700;
}

.us-alerts-empty {
  padding: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.us-alert-item {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  border-top: 1px solid #f3f4f6;
  font-size: 0.8rem;
}

.us-alert-item:hover {
  background: #f9fafb;
}

.us-alert-item-unread {
  border-left: 3px solid #dc2626;
  background: #fff7f7;
}

.us-alert-summary,
.us-alert-details {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 100%;
  text-align: left;
}

.us-alert-details {
  margin-top: 0.2rem;
  padding: 0.45rem 0.55rem;
  border-radius: 0.35rem;
  background: #f9fafb;
  color: #4b5563;
}

.us-alert-context {
  overflow: hidden;
  color: #6b7280;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.us-alert-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.us-alert-secondary,
.us-alert-resolve {
  border: 1px solid #d1d5db;
  border-radius: 0.35rem;
  padding: 0.2rem 0.45rem;
  font-size: 0.7rem;
  font-weight: 600;
}

.us-alert-secondary {
  color: #4b5563;
}

.us-alert-resolve {
  color: #166534;
}

.us-alert-resolve:hover {
  border-color: #16a34a;
  color: #166534;
}

.us-alert-order {
  color: #111827;
  font-weight: 700;
}

@media (max-width: 768px) {
  .navbar-search-group {
    display: none;
  }
}

/* Hide the top navbar when printing the recap (remove 图二 on paper) */
@media print {
  .navbar {
    display: none !important;
  }
}
</style>
